import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { cartSelectors, cartOperations } from '../state/cart';
import cartActions from '../state/cart/actions';
import {
  trackMaxPendingOperationsDrivenCartReloadAttempts,
  trackPendingOperationsDrivenCartReloadAttempts,
} from '../apis/newRelicTracking';
import { RELOAD_DELAY_MS } from '../constants/cartLoad';

// hook that makes sure the right cart data is loaded, and returns a boolean indicator of whether there is cart data loaded.
export default function useCartLoader(tenant, shopperId, locale) {
  const isCartLoaded = useSelector(state =>
    cartSelectors.selectIsLoaded(state.cart)
  );
  const isOutdated = useSelector(state =>
    cartSelectors.selectIsOutdated(state.cart)
  );
  const loadedTenant = useSelector(state =>
    cartSelectors.selectTenant(state.cart)
  );
  const loadedShopperId = useSelector(state =>
    cartSelectors.selectShopperId(state.cart)
  );
  const pendingOperations = useSelector(state =>
    cartSelectors.selectPendingOperations(state.cart)
  );
  const pendingOperationReloadAttempts = useSelector(state =>
    cartSelectors.selectPendingOperationReloadAttemptCount(state.cart)
  );
  const didCartLoadHitMaxReloadAttempts = useSelector(state =>
    cartSelectors.selectDidCartLoadHitMaxReloadAttempts(state.cart)
  );

  const dispatch = useDispatch();

  const noCartOrCurrentlyLoadedCartIsWrong =
    !isCartLoaded ||
    isOutdated ||
    tenant !== loadedTenant ||
    shopperId !== loadedShopperId;

  useEffect(() => {
    if (noCartOrCurrentlyLoadedCartIsWrong) {
      if (shopperId) {
        dispatch(cartOperations.load(tenant, shopperId, locale));
      } else {
        dispatch(cartOperations.unload());
      }
    }
  }, [
    isOutdated,
    dispatch,
    tenant,
    loadedTenant,
    shopperId,
    loadedShopperId,
    noCartOrCurrentlyLoadedCartIsWrong,
    locale,
  ]);

  const hasPendingOperations = pendingOperations?.length > 0;
  const shouldTryReloadingForPendingOperations =
    !noCartOrCurrentlyLoadedCartIsWrong &&
    hasPendingOperations &&
    !didCartLoadHitMaxReloadAttempts;

  useEffect(() => {
    if (shouldTryReloadingForPendingOperations) {
      const timer = setTimeout(() => {
        dispatch(cartOperations.load(tenant, shopperId, locale, true));
        dispatch(cartActions.INCREMENT_PENDING_OPERATION_RELOAD_ATTEMPTS());
      }, RELOAD_DELAY_MS);

      return () => clearTimeout(timer);
    }
    // else
    if (!hasPendingOperations) {
      if (
        !noCartOrCurrentlyLoadedCartIsWrong &&
        pendingOperationReloadAttempts > 0
      ) {
        trackPendingOperationsDrivenCartReloadAttempts(
          { reloadAttempts: pendingOperationReloadAttempts },
          shopperId,
          tenant
        );
      }
      // reset reload attempts
      dispatch(cartActions.RESET_PENDING_OPERATION_RELOAD_ATTEMPTS());
    } else if (!noCartOrCurrentlyLoadedCartIsWrong) {
      // we got here because we have reached MAX_RELOAD_ATTEMPTS. Rather than reset pendingOperationReloadAttempts and risk looping cart reads, we will capture the fact that this has
      // occurred and let the cart be displayed.
      trackMaxPendingOperationsDrivenCartReloadAttempts(
        { pendingOperations },
        shopperId,
        tenant
      );
    }
  }, [
    dispatch,
    tenant,
    shopperId,
    locale,
    shouldTryReloadingForPendingOperations,
    pendingOperations,
    hasPendingOperations,
    noCartOrCurrentlyLoadedCartIsWrong,
    pendingOperationReloadAttempts,
  ]);

  /*
  readyForDisplay tracks whether or not we're ready to render the rest of cartUI - i.e. the cart has reached a "reasonably stable" state

  This is intended to be set to true when, at least once, the cart was:
  * Correctly loaded
  * Not out-of-date (i.e. doesn't have other changes that are likely to be applied)

  Blocking until this point should minimize page jank and prevent other parts of the app from acting on a cart that's still in a volatile state.

  The main things that need to happen in useCartLoader before we reach that state (in order):
  * Load the cart
  * Complete any reloads for pendingOperations
  */
  const [readyForDisplay, setReadyForDisplay] = useState(
    !noCartOrCurrentlyLoadedCartIsWrong &&
      !shouldTryReloadingForPendingOperations
  );

  // useEffect responsible for determining when ready to display a cart experience after cart load.
  // for customers who land in cart with no pendingOperations, set ready for display to true.
  // for customers who land in cart with pendingOperations, only set ready for display to true when we have completed our reload attempts.
  useEffect(() => {
    // Once we've reached any point in time where the cart has finished loading (which implies it's no longer outdated)
    // and lacks pendingOperations, then signal we're ready for display. Theoretically, this should never go back
    // to false after it's been set to true.
    if (
      !noCartOrCurrentlyLoadedCartIsWrong &&
      !shouldTryReloadingForPendingOperations
    ) {
      setReadyForDisplay(true);
    }
  }, [
    noCartOrCurrentlyLoadedCartIsWrong,
    shouldTryReloadingForPendingOperations,
  ]);

  return readyForDisplay;
}
